import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgApexchartsModule } from 'ng-apexcharts';
import { FormsModule } from '@angular/forms';

import { DashboardService } from './service/dashboard.service';
import { DashboardSummary } from './interface/dashboard';

export type ApexChartOptions = {
  series: any[];
  chart: { type: string; height: number; toolbar?: { show: boolean } };
  xaxis?: { categories: string[] };
  labels?: string[];
  legend?: { position: string };
  dataLabels?: { enabled: boolean };
  colors?: string[];
  stroke?: { curve: string; width?: number[] };
  fill?: { type?: string; opacity?: number };
  plotOptions?: any;
  responsive?: any[];
};


@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss']
})

/**
 * Dashboard Component
 */
export class DashboardComponent implements OnInit {

  stats: any;
  dashboardData!: DashboardSummary;

  loading: boolean = false;


  constructor(
    private dashboardService: DashboardService
  ) { }

  ngOnInit() {
    this.getDashboardSummary();
  }

  // =====================================
  // GET DASHBOARD SUMMARY
  // =====================================

  getDashboardSummary() {

    this.loading = true;

    this.dashboardService
      .getDashboardSummary()
      .subscribe({

        next: (res) => {

          this.dashboardData = res.data;

          this.loading = false;

        },

        error: (err) => {

          console.log(err);

          this.loading = false;

        }

      });

  }




  // -----------------------
  // Quick Stats (mock data)
  // -----------------------
  quick = {
    todayOrders: 38,
    monthOrders: 840,
    totalRevenue: 1285000,
    totalCustomers: 1432,
    pendingOrders: 56,
    returnsCount: 12
  };

  // -----------------------
  // Sales Trend (Line/Bar)
  // -----------------------
  salesChart: Partial<ApexChartOptions> = {
    series: [
      { name: 'Revenue', data: [12000, 18500, 14200, 22300, 19800, 25400, 31000] },
      { name: 'Orders', data: [120, 150, 130, 170, 160, 200, 230] }
    ],
    chart: { type: 'line', height: 320, toolbar: { show: false } },
    xaxis: { categories: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] },
    dataLabels: { enabled: false },
    stroke: { curve: 'smooth', width: [3, 3] },
    colors: ['#0d6efd', '#20c997'],
    legend: { position: 'top' },
    responsive: [
      { breakpoint: 576, options: { legend: { position: 'bottom' } } }
    ]
  };

  // -----------------------
  // Payment Breakdown (Donut)
  // -----------------------
  paymentChart: Partial<ApexChartOptions> = {
    series: [44100, 22500, 13800], // revenue per method
    labels: ['Cash On Delivery', 'Card', 'Wallet'],
    chart: { type: 'donut', height: 320, toolbar: { show: false } },
    legend: { position: 'bottom' },
    dataLabels: { enabled: true },
    colors: ['#0d6efd', '#198754', '#ffc107']
  };

  // -----------------------
  // Order Status Breakdown (Donut)
  // -----------------------
  statusChart: Partial<ApexChartOptions> = {
    series: [56, 320, 410, 22], // orders count per status
    labels: ['Pending', 'Processing', 'Delivered', 'Cancelled'],
    chart: { type: 'donut', height: 320, toolbar: { show: false } },
    legend: { position: 'bottom' },
    dataLabels: { enabled: true },
    colors: ['#6c757d', '#0d6efd', '#20c997', '#dc3545']
  };

  // -----------------------
  // Top Customers (mock)
  // -----------------------
  topCustomers = [
    { name: 'Ali Raza', email: 'ali@example.com', orders: 34, spend: 245000 },
    { name: 'Sara Khan', email: 'sara@example.com', orders: 29, spend: 198500 },
    { name: 'Usman Tariq', email: 'usman@example.com', orders: 26, spend: 176200 },
    { name: 'Hina Iqbal', email: 'hina@example.com', orders: 22, spend: 152900 },
    { name: 'Danish Ali', email: 'danish@example.com', orders: 20, spend: 139400 }
  ];

  // -----------------------
  // Inventory Snapshot (mock)
  // -----------------------
  inventoryValuation = {
    totalQty: 842,
    totalCost: 385000,
    totalRetail: 769500
  };

  lowStock = [
    { sku: 'SKU-101', name: 'Ocean Blue Perfume', qty: 3 },
    { sku: 'SKU-244', name: 'Matte Lipstick – Rose', qty: 5 },
    { sku: 'SKU-332', name: 'Hydrating Face Wash', qty: 7 },
    { sku: 'SKU-519', name: 'Almond Body Lotion', qty: 8 }
  ];

  // -----------------------
  // Returns / Refunds (mock)
  // -----------------------
  returns = { count: 12, refundAmount: 58600 };

  // -----------------------
  // Recent Activity (mock)
  // -----------------------
  recentOrders = [
    { id: 'INV-10121', customer: 'Ali Raza', amount: 12500, status: 'Processing', date: '2025-01-30' },
    { id: 'INV-10122', customer: 'Sara Khan', amount: 9800, status: 'Delivered', date: '2025-01-30' },
    { id: 'INV-10123', customer: 'Usman Tariq', amount: 13200, status: 'Pending', date: '2025-01-30' },
    { id: 'INV-10124', customer: 'Hina Iqbal', amount: 7600, status: 'Delivered', date: '2025-01-29' },
    { id: 'INV-10125', customer: 'Danish Ali', amount: 15400, status: 'Cancelled', date: '2025-01-29' }
  ];

  recentReturns = [
    { id: 'RET-9001', customer: 'Hina Iqbal', amount: 3200, reason: 'Damaged', date: '2025-01-29' },
    { id: 'RET-9002', customer: 'Ali Raza', amount: 1800, reason: 'Size Issue', date: '2025-01-28' },
    { id: 'RET-9003', customer: 'Sara Khan', amount: 2400, reason: 'Wrong Item', date: '2025-01-27' }
  ];

  // -----------------------------------------------------
  // TODO: Replace mock with API calls and shared date filter
  // -----------------------------------------------------
}